import React, { useState, useEffect } from 'react';
import { Bot, Loader2, Send, FileText, Paperclip, Download, Save, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { escritosIaAPI, expedientesAPI, adjuntosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const asList = (d) => (Array.isArray(d) ? d : (d?.items || d?.data || []));
const snippet = (t, n = 70) => (t ? (t.length > n ? t.slice(0, n) + '…' : t) : '');

export const EncargarIADialog = ({ expediente }) => {
  const [open, setOpen] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [modelo, setModelo] = useState('claude-sonnet-4-6');
  const [tipo, setTipo] = useState('escrito_generico');
  const [actuaciones, setActuaciones] = useState([]);
  const [actuacionId, setActuacionId] = useState('none');
  const [adjuntos, setAdjuntos] = useState([]);
  const [instrucciones, setInstrucciones] = useState('');
  const [generando, setGenerando] = useState(false);
  const [escrito, setEscrito] = useState('');
  const [resultInfo, setResultInfo] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    escritosIaAPI.modelos()
      .then((r) => { setModelos(r.data.modelos || []); setTipos(r.data.tipos || []); })
      .catch(() => {});
    expedientesAPI.getActuaciones(expediente.id)
      .then((r) => setActuaciones(asList(r.data)))
      .catch(() => setActuaciones([]));
  }, [open, expediente.id]);

  useEffect(() => {
    if (actuacionId && actuacionId !== 'none') {
      adjuntosAPI.getByActuacion(actuacionId)
        .then((r) => setAdjuntos(asList(r.data)))
        .catch(() => setAdjuntos([]));
    } else {
      setAdjuntos([]);
    }
  }, [actuacionId]);

  const tipoLabel = () => (tipos.find((t) => t.value === tipo)?.label) || 'Escrito';

  const verAdjunto = async (id) => {
    try {
      const r = await escritosIaAPI.verAdjunto(id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      window.open(url, '_blank');
    } catch {
      toast({ title: 'Error', description: 'No se pudo abrir el adjunto', variant: 'destructive' });
    }
  };

  const generar = async () => {
    setGenerando(true);
    setEscrito('');
    setResultInfo(null);
    try {
      const { data } = await escritosIaAPI.generar({
        expediente_id: expediente.id,
        task_type: tipo,
        modelo,
        actuacion_id: actuacionId !== 'none' ? parseInt(actuacionId, 10) : null,
        instructions: instrucciones,
        incluir_adjuntos: true,
      });
      setEscrito(data.escrito || '');
      setResultInfo(data);
      const usados = (data.adjuntos_incluidos || []).length;
      toast({
        title: 'Escrito generado',
        description: `${data.tipo} · ${data.modelo}${usados ? ` · ${usados} adjunto(s) usados` : ''}`,
      });
    } catch (e) {
      const d = e.response?.data?.detail || 'No se pudo generar el escrito';
      toast({ title: 'Error', description: typeof d === 'string' ? d : 'Error al generar', variant: 'destructive' });
    } finally {
      setGenerando(false);
    }
  };

  const guardarComoActuacion = async () => {
    setGuardando(true);
    try {
      await expedientesAPI.createActuacion(expediente.id, {
        titulo: `[IA] ${tipoLabel()}`,
        fecha: new Date().toISOString(),
        tipo: 'Escrito',
        descripcion: escrito,
        observaciones: `Generado por IA (${modelo})`,
      });
      toast({ title: 'Guardado', description: 'El escrito se guardó como actuación del expediente.' });
    } catch {
      toast({ title: 'Error', description: 'No se pudo guardar la actuación', variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const descargarWord = async () => {
    try {
      const { data } = await escritosIaAPI.word({ titulo: tipoLabel(), contenido: escrito });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tipoLabel().replace(/ /g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Error', description: 'No se pudo descargar el Word', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="bg-purple-700 text-white hover:bg-purple-800" data-testid="generar-escrito-ia-btn">
          <Bot className="w-4 h-4 mr-2" />GENERAR ESCRITO IA
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl w-[92vw] h-[90vh] flex flex-col" data-testid="escrito-ia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-purple-700" />Generar escrito con IA (Claude)</DialogTitle>
          <DialogDescription>
            Expediente {expediente.numero_expediente} — {expediente.titulo}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
          {/* IZQUIERDA: configuración */}
          <div className="flex flex-col gap-4 overflow-y-auto pr-2">
            <div>
              <Label>Modelo de IA</Label>
              <Select value={modelo} onValueChange={setModelo}>
                <SelectTrigger data-testid="escrito-modelo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {modelos.map((m) => (
                    <SelectItem key={m.value} value={m.value} data-testid={`escrito-modelo-${m.value}`}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tipo de escrito</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger data-testid="escrito-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tipos.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`escrito-tipo-${t.value}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Actuación base (opcional)</Label>
              <Select value={actuacionId} onValueChange={setActuacionId}>
                <SelectTrigger data-testid="escrito-actuacion-select"><SelectValue placeholder="Sin actuación base" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin actuación base —</SelectItem>
                  {actuaciones.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)} data-testid={`escrito-actuacion-${a.id}`}>
                      {(a.fecha ? String(a.fecha).slice(0, 10) : '')} · {a.tipo || 'Actuación'} · {snippet(a.descripcion || a.titulo)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {actuacionId !== 'none' && (
              <div className="rounded-md border border-slate-200 p-3" data-testid="escrito-adjuntos-list">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <Paperclip className="w-4 h-4" />Adjuntos de la actuación ({adjuntos.length})
                </div>
                {adjuntos.length === 0 ? (
                  <p className="text-xs text-slate-500">Sin adjuntos. Se usarán solo el texto de la actuación y los datos del expediente.</p>
                ) : (
                  <ul className="space-y-1">
                    {adjuntos.map((adj) => (
                      <li key={adj.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-1 truncate"><FileText className="w-3 h-3 shrink-0" />{adj.nombre}</span>
                        <Button type="button" variant="ghost" size="sm" className="h-6 px-2" onClick={() => verAdjunto(adj.id)} data-testid={`escrito-ver-adjunto-${adj.id}`}>
                          <Eye className="w-3 h-3 mr-1" />Ver
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-slate-400 mt-2">Los PDF/DOCX se convierten a Markdown y se envían como contexto a la IA.</p>
              </div>
            )}

            <div className="flex-1 flex flex-col">
              <Label>Instrucciones para la IA</Label>
              <Textarea
                className="flex-1 min-h-[140px]"
                value={instrucciones}
                onChange={(e) => setInstrucciones(e.target.value)}
                placeholder="Qué debe redactar, argumentos clave, cuantías, plazos, particularidades del caso…"
                data-testid="escrito-instrucciones-input"
              />
            </div>

            <Button onClick={generar} disabled={generando} className="bg-purple-700 hover:bg-purple-800" data-testid="escrito-generar-btn">
              {generando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {generando ? 'Generando… (puede tardar)' : 'Generar escrito'}
            </Button>
          </div>

          {/* DERECHA: resultado */}
          <div className="flex flex-col gap-3 min-h-0">
            <div className="flex items-center justify-between">
              <Label>Escrito generado</Label>
              {resultInfo && (
                <span className="text-[11px] text-slate-400">
                  {resultInfo.modelo}{(resultInfo.adjuntos_incluidos || []).length ? ` · ${resultInfo.adjuntos_incluidos.length} adj.` : ''}
                </span>
              )}
            </div>
            <Textarea
              className="flex-1 min-h-0 font-mono text-sm"
              value={escrito}
              onChange={(e) => setEscrito(e.target.value)}
              placeholder="El escrito aparecerá aquí. Podrás editarlo antes de guardar o descargar."
              data-testid="escrito-resultado-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={descargarWord} disabled={!escrito} data-testid="escrito-word-btn">
                <Download className="w-4 h-4 mr-2" />Descargar Word
              </Button>
              <Button onClick={guardarComoActuacion} disabled={!escrito || guardando} className="bg-emerald-600 hover:bg-emerald-700" data-testid="escrito-guardar-btn">
                {guardando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar como actuación
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
